import React, { ReactNode } from 'react';
import { Container, Row, Col, Stack, Form, Button } from 'react-bootstrap';
import DownloadJobstasApp from 'components/Home/DownloadJobstasApp';
import { useFormik } from 'formik';
import { contactUsInitialValues, contactUsSchema } from 'constants/schemaValidations';
import { ContactUsInitialValues } from 'constants/interfaces';
import useTranslate from 'hooks/useTranslate';
import { useContactUsMutation } from 'services';
import toast from 'react-hot-toast';
import { getPhoneFormat } from 'utils';
import { contactUsDetails } from 'constants';
import parse from 'html-react-parser';
import '../styles.scss';

export const ContactUs: React.FC = () => {
	const { translate } = useTranslate();
	const { mutateAsync: contactUsMutation, isPending } = useContactUsMutation();

	const formik = useFormik({
		initialValues: { ...contactUsInitialValues },
		validationSchema: contactUsSchema(translate),
		onSubmit: async (values, { resetForm }) => {
			const { fullName: name, email, phone: phoneNumber, description } = values;

			contactUsMutation({
				name,
				phoneNumber: phoneNumber.toString(),
				email,
				description: description.trim(),
			}).then((res: any) => {
				if (res?.success) {
					toast.success(res?.message);
					resetForm();
				}
			});
		},
	});

	const handleChange = (e: any) => {
		const inputValue = getPhoneFormat(e.target.value);
		if (inputValue) {
			formik.setFieldValue('phone', e.target.value);
		}
	};

	const renderError = <T extends keyof ContactUsInitialValues>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<section className="static-page bg-white">
			<div className="static-page-banner">
				<div className="static-page-banner-bg contact-banner h-100 w-100 d-flex align-items-center justify-content-center position-relative">
					<div className="static-page-banner-bg-overlay w-100 h-100 position-absolute top-0 left-0"></div>
					<h2 className="fw-bold text-white mb-0">{translate('Footer.contactUs')}</h2>
				</div>
			</div>
			<Container>
				<div className="static-page-content py-80">
					<div className="contact-heading text-center mx-auto">
						<h3 className="fw-bold">{translate('Footer.FindUs')}</h3>
						<p className="mb-0 px-sm-5">{translate('Footer.findUsDesc')} </p>
					</div>
					<div className="contact-content">
						<Row className="contact-content-inner">
							<Col md={6}>
								<h4 className="fw-bold mb-3">{translate('Footer.getProfessional')}</h4>
								<p className="mb-4">{translate('Common.contactUsPageTitle')}</p>
								<Stack className="contact-info">
									<div className="d-flex align-items-center gap-3">
										<div className="contact-info-icon px-lg-3">
											<i className="ri-mail-fill"></i>
										</div>
										<div>
											<h6 className="fw-bold">{translate('Footer.emailUs')}</h6>
											<p className="fw-bold mb-0">{contactUsDetails.email}</p>
										</div>
									</div>
									{/* <div className="d-flex align-items-center gap-3">
										<div className="contact-info-icon px-lg-3">
											<i className="ri-phone-fill"></i>
										</div>
										<div>
											<h6 className="fw-bold">{translate('Footer.contactUs')}</h6>
											<p className="fw-bold mb-0">{contactUsDetails.phone}</p>
										</div>
									</div>
									<div className="d-flex align-items-center gap-3">
										<div className="contact-info-icon px-lg-3">
											<i className="ri-map-pin-fill"></i>
										</div>
										<div>
											<h6 className="fw-bold">{translate('Resume.address')}</h6>
											<p className="fw-bold mb-0">{parse(contactUsDetails.address)}</p>
										</div>
									</div> */}
								</Stack>
							</Col>
							<Col md={6}>
								<div className="contact-form">
									<Form onSubmit={formik.handleSubmit}>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('PlaceHolder.fullName')}</Form.Label>
											<Form.Control
												type="text"
												placeholder={translate('PlaceHolder.enterFullName')}
												name="fullName"
												className="rounded-pill"
												autoComplete="off"
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													let inputValue = e.target.value;

													// Remove leading spaces if present
													inputValue = inputValue.replace(/^\s+/, '');

													// Allow only one space between words
													inputValue = inputValue.replace(/\s{2,}/g, ' ');
													formik.setFieldValue(`fullName`, inputValue);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.fullName}
											/>
											{renderError('fullName')}
										</Form.Group>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('PlaceHolder.email')}</Form.Label>
											<Form.Control
												type="email"
												placeholder={translate('PlaceHolder.enterEmailAddress')}
												name="email"
												className="rounded-pill"
												autoComplete="off"
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													let inputValue = e.target.value;

													// Remove leading spaces if present
													inputValue = inputValue.replace(/^\s+/, '');

													// Allow only one space between words
													inputValue = inputValue.replace(/\s{2,}/g, ' ');
													formik.setFieldValue(`email`, inputValue);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.email}
											/>
											{renderError('email')}
										</Form.Group>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">{translate('PlaceHolder.phone')}</Form.Label>
											<Form.Control
												type="text"
												placeholder={translate('PlaceHolder.enterPhone')}
												name="phone"
												className="rounded-pill"
												autoComplete="off"
												onChange={handleChange}
												onBlur={formik.handleBlur}
												value={formik.values.phone}
												maxLength={12}
											/>
											{renderError('phone')}
										</Form.Group>
										<Form.Group className="mb-4 position-relative">
											<Form.Label className="lh-1">
												{translate('PlaceHolder.description')}
											</Form.Label>
											<Form.Control
												as="textarea"
												rows={3}
												placeholder={translate('PlaceHolder.writeHere')}
												name="description"
												autoComplete="off"
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													let inputValue = e.target.value;

													// Remove leading spaces if present
													inputValue = inputValue.replace(/^\s+/, '');

													// Allow only one space between words
													inputValue = inputValue.replace(/\s{2,}/g, ' ');
													formik.setFieldValue(`description`, inputValue);
												}}
												onBlur={formik.handleBlur}
												value={formik.values.description}
											/>
											{renderError('description')}
										</Form.Group>
										<Button
											disabled={isPending}
											type="submit"
											className="filled-btn rounded-pill w-100 mt-2"
										>
											{translate('Common.save')}
										</Button>
									</Form>
								</div>
							</Col>
						</Row>
					</div>
				</div>
			</Container>
			<DownloadJobstasApp />
		</section>
	);
};

export default ContactUs;
